import getNetworkInfo from 'mini-network-info/lib/utils/getNetworkInfo';
import { CustomData } from 'mini-app-context/lib/schema/CustomData';
import { DatapointOptions } from 'owa-analytics-actions';
import { isPWA } from 'mini-device-utils/lib/utils/isPWA';
import { PerformanceDatapoint } from 'owa-analytics';
import { stripEmailAddressFromPath } from 'mini-common-utils/lib/utils/stripEmailAddressFromPath';

interface DatapointProps {
    datapointName: string;
    customData?: CustomData;
    options?: DatapointOptions;
}

interface InitialRenderDatapointCustomDataType {
    userAgent: string;
    screenWidth: number;
    screenHeight: number;
    isPWA: boolean;
    refUrl: string;
    networkInfo: string;
    domInteractive: number;
    domContentLoaded: number;
    domComplete: number;
}

/**
 * Logs PerformanceDatapoint to track time taken for initial render.
 * This is the total time taken from when the URL fetch is started to when this piece of
 * code is executed.
 * Also logs many custom data like user-agent, network info, screen size,
 * PWA is installed or not, time taken for DOM to get interactive, loaded and complete.
 */
export default function logInitialRenderPerfDatapoint(props: DatapointProps) {
    if (!window) {
        return;
    }

    // https://developers.google.com/web/fundamentals/performance/critical-rendering-path/measure-crp
    const { timing } = window.performance ?? {};

    if (!timing) {
        return;
    }
    const domInteractive = timing.domInteractive - timing.domLoading; // domInteractive marks when DOM is ready
    const domContentLoaded = timing.domContentLoadedEventStart - timing.domLoading; // domContentLoaded typically marks when both the DOM and CSSOM are ready
    const domComplete = timing.domComplete - timing.domLoading; // domComplete marks when the page and all of its sub resources are ready

    let networkInfo: string;
    try {
        networkInfo = JSON.stringify(getNetworkInfo());
    } catch {
        networkInfo = 'network info could not be packed';
    }

    const defaultOptions: DatapointOptions = { isCore: true };
    const defaultCustomData: InitialRenderDatapointCustomDataType = {
        // encoding the userAgent to prevent false detection of chrome version as an ip address
        userAgent: window.navigator ? btoa && btoa(window.navigator.userAgent) : null,
        screenWidth: screen ? screen.width : null,
        screenHeight: screen ? screen.height : null,
        isPWA: isPWA(),
        refUrl: stripEmailAddressFromPath(window.location.pathname),
        networkInfo,
        domInteractive,
        domContentLoaded,
        domComplete,
    };

    const initialRenderCompleteOptions: DatapointOptions = { ...defaultOptions, ...props.options };
    const initialRenderCompleteCustomData: CustomData = {
        ...defaultCustomData,
        ...props.customData,
    };

    const initialRenderCompletePerfDP = new PerformanceDatapoint(
        props.datapointName,
        initialRenderCompleteOptions
    );
    initialRenderCompletePerfDP.addCustomData(initialRenderCompleteCustomData);

    // Total time taken till this code is run from when the initial url fetch starts
    initialRenderCompletePerfDP.end(new Date().getTime() - (timing.fetchStart ?? 0));
}
